import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { useState } from 'react';
import { cmp } from '@guardian/consent-management-platform';
import { canShowRRBanner, canShowPuzzlesBanner, ReaderRevenueBanner, PuzzlesBanner, } from '@root/src/web/components/StickyBottomBanner/ReaderRevenueBanner';
import { getAlreadyVisitedCount } from '@root/src/web/lib/alreadyVisited';
import { useOnce } from '@root/src/web/lib/useOnce';
import { pickMessage, } from '@root/src/web/lib/messagePicker';
import { useSignInGateWillShow } from '@root/src/web/lib/useSignInGateWillShow';
import { BrazeBanner, canShow as canShowBrazeBanner } from './BrazeBanner';
const getBannerLastClosedAt = (key) => {
    const item = localStorage.getItem(`gu.prefs.${key}`);
    if (item) {
        const parsedItem = JSON.parse(item);
        return parsedItem.value;
    }
    return item;
};
const DEFAULT_BANNER_TIMEOUT_MILLIS = 2000;
const buildCmpBannerConfig = () => ({
    candidate: {
        id: 'cmpUi',
        canShow: () => cmp
            .willShowPrivacyMessage()
            .then((result) => result ? { show: true, meta: undefined } : { show: false }),
        show: () => {
            // New CMP is not a react component and is shown outside of react's world
            // so render nothing if it will show
            return null;
        },
    },
    timeoutMillis: null,
});
const buildRRBannerConfigWith = ({ id, BannerComponent, canShowFn, isEnabled, }) => {
    return (CAPI, isSignedIn, asyncCountryCode, isPreview, asyncArticleCount, signInGateWillShow = false) => {
        return {
            candidate: {
                id,
                canShow: () => canShowFn({
                    remoteBannerConfig: isEnabled(CAPI.config.switches),
                    isSignedIn,
                    asyncCountryCode,
                    contentType: CAPI.contentType,
                    sectionName: CAPI.sectionName,
                    shouldHideReaderRevenue: CAPI.shouldHideReaderRevenue,
                    isMinuteArticle: CAPI.pageType.isMinuteArticle,
                    isPaidContent: CAPI.pageType.isPaidContent,
                    isSensitive: CAPI.config.isSensitive,
                    tags: CAPI.tags,
                    contributionsServiceUrl: CAPI.contributionsServiceUrl,
                    alreadyVisitedCount: getAlreadyVisitedCount(),
                    engagementBannerLastClosedAt: getBannerLastClosedAt('engagementBannerLastClosedAt'),
                    subscriptionBannerLastClosedAt: getBannerLastClosedAt('subscriptionBannerLastClosedAt'),
                    section: CAPI.config.section,
                    isPreview,
                    idApiUrl: CAPI.config.idApiUrl,
                    signInGateWillShow,
                    asyncArticleCount,
                }),
                show: ({ meta, module, email }) => () => (_jsx(BannerComponent, { meta: meta, module: module, email: email }, void 0)),
            },
            timeoutMillis: DEFAULT_BANNER_TIMEOUT_MILLIS,
        };
    };
};
const buildPuzzlesBannerConfig = buildRRBannerConfigWith({
    id: 'puzzles-banner',
    BannerComponent: PuzzlesBanner,
    canShowFn: canShowPuzzlesBanner,
    isEnabled: (switches) => switches.puzzlesBanner,
});
const buildReaderRevenueBannerConfig = buildRRBannerConfigWith({
    id: 'reader-revenue-banner',
    BannerComponent: ReaderRevenueBanner,
    canShowFn: canShowRRBanner,
    isEnabled: (switches) => switches.remoteBanner,
});
const buildBrazeBanner = (brazeMessages) => ({
    candidate: {
        id: 'braze-banner',
        canShow: () => canShowBrazeBanner(brazeMessages),
        show: (meta) => () => _jsx(BrazeBanner, { meta: meta }, void 0),
    },
    timeoutMillis: DEFAULT_BANNER_TIMEOUT_MILLIS,
});
export const StickyBottomBanner = ({ isSignedIn, asyncCountryCode, CAPI, brazeMessages, isPreview, asyncArticleCount, }) => {
    const [SelectedBanner, setSelectedBanner] = useState(null);
    const signInGateWillShow = useSignInGateWillShow({ isSignedIn, CAPI });
    useOnce(() => {
        const CMP = buildCmpBannerConfig();
        const puzzlesBanner = buildPuzzlesBannerConfig(CAPI, isSignedIn, asyncCountryCode, isPreview, asyncArticleCount);
        const readerRevenue = buildReaderRevenueBannerConfig(CAPI, isSignedIn, asyncCountryCode, isPreview, asyncArticleCount, signInGateWillShow);
        const brazeBanner = buildBrazeBanner(brazeMessages);
        const bannerConfig = {
            candidates: [CMP, puzzlesBanner, readerRevenue, brazeBanner],
            name: 'banner',
        };
        pickMessage(bannerConfig)
            .then((PickedBanner) => setSelectedBanner(PickedBanner))
            .catch((e) => console.error(`StickyBottomBanner pickMessage - error: ${e}`));
    }, [isSignedIn, asyncCountryCode, CAPI, brazeMessages, asyncArticleCount]);
    if (SelectedBanner) {
        return _jsx(SelectedBanner, {}, void 0);
    }
    return null;
};
